// ============================================================================
// Pig daily report: "Days since last move"
// ============================================================================
//
// Counted from the most recent pig daily for the group where "Was group
// moved?" was Yes, relative to the date of the report being filled in. Dates
// are plain 'YYYY-MM-DD' strings (the report date is todayCentralISO-based),
// so the count is a calendar-day difference with no timezone arithmetic.
// ============================================================================

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayNumber(iso: string): number | null {
  const m = ISO_DATE_RE.exec(String(iso || '').slice(0, 10));
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? Math.round(t / 86400000) : null;
}

export type DaysSinceMove =
  | {kind: 'days'; days: number; lastMoveDate: string}
  | {kind: 'none'} // no move on record at or before the report date
  | {kind: 'unknown'}; // the report date is older than every loaded move row, or no usable date

/**
 * @param moveDates  dates of group-moved = Yes reports for this group (any order).
 * @param reportDate the report's date.
 * @param complete   true when moveDates is the group's entire move history; false
 *                   when it is a newest-N window, in which case a report date
 *                   earlier than every row is 'unknown' rather than 'none'.
 */
export function daysSinceLastMove(
  moveDates: readonly (string | null | undefined)[] | null | undefined,
  reportDate: string | null | undefined,
  complete = true,
): DaysSinceMove {
  const report = dayNumber(reportDate || '');
  if (report == null) return {kind: 'unknown'};
  let best: {n: number; iso: string} | null = null;
  let anyRow = false;
  for (const d of moveDates || []) {
    const n = dayNumber(d || '');
    if (n == null) continue;
    anyRow = true;
    if (n > report) continue;
    if (!best || n > best.n) best = {n, iso: String(d).slice(0, 10)};
  }
  if (best) return {kind: 'days', days: report - best.n, lastMoveDate: best.iso};
  if (anyRow && !complete) return {kind: 'unknown'};
  return {kind: 'none'};
}

/** Owner's scale: 0-7 none; 8-13 😬; 14-21 😢; 22 or more 🤮😡💩🪱. */
export function moveEmoji(days: number): string {
  if (!Number.isFinite(days) || days <= 7) return '';
  if (days <= 13) return '😬';
  if (days <= 21) return '😢';
  return '🤮😡💩🪱';
}

/** One-line text for the form. */
export function daysSinceMoveText(result: DaysSinceMove): string {
  if (result.kind === 'none') return 'No move recorded';
  if (result.kind === 'unknown') return 'Unknown';
  const emoji = moveEmoji(result.days);
  return `${result.days} day${result.days === 1 ? '' : 's'}${emoji ? ' ' + emoji : ''}`;
}
